import i18n from '../../i18n/index.js';
import { api } from '../api.js';
import { useOffline } from '../../stores/offline.js';
import { can, useSession } from '../../stores/session.js';
import { db } from './db.js';
import { nextOfflineToken, offlineCheck } from './permit.js';

/**
 * Open orders without internet (Settings → Offline & displays → "Hold orders and send them to
 * the kitchen offline"). While online, the device keeps a full copy of the outlet's open orders
 * (GET /sync/holds) so any of them can be resumed offline. Orders held offline are stored here
 * and in the outbox; the server merges them when they sync (POST /sync/holds) and never
 * overwrites a newer version from another device: that becomes a second open order, flagged.
 */
const uuid = () => crypto.randomUUID();

const discountOf = (d) =>
  d
    ? d.type === 'percent'
      ? { type: 'percent', valueBp: d.valueBp }
      : { type: 'flat', valuePaise: d.valuePaise }
    : null;

/** A server bill (full DTO) as a row of the local copy. */
const serverRow = (b) => ({
  ...b,
  serverId: b.id,
  local: false,
  baseUpdatedAt: b.updatedAt,
  itemCount: b.lines.reduce((n, l) => n + l.qty, 0),
  grandTotalPaise: b.totals?.grandTotalPaise ?? 0,
});

const eligible = () => {
  const me = useSession.getState().me;
  return Boolean(me?.device?.id) && can(me, 'pos.bill');
};

/** Refresh the copy of the outlet's open orders (keeps orders held here that haven't synced). */
export async function refreshHolds() {
  if (!eligible() || !useOffline.getState().reachable) return;
  try {
    const r = await api.get('/sync/holds');
    await db.transaction('rw', db.holds, db.outbox, async () => {
      const waiting = new Set((await db.outbox.where('kind').equals('hold').toArray()).map((o) => o.clientUuid));
      const keep = (await db.holds.toArray()).filter((h) => h.local && waiting.has(h.clientUuid));
      await db.holds.clear();
      await db.holds.bulkPut([...r.items.map(serverRow), ...keep]);
    });
  } catch {
    /* keep the old copy */
  }
}

let timer = null;
/** Refresh soon (debounced): an order changed somewhere. */
export function scheduleHoldsRefresh(ms = 2_000) {
  if (!eligible()) return;
  clearTimeout(timer);
  timer = setTimeout(() => void refreshHolds(), ms);
}

/** Open orders known to this device, newest first (offline list). */
export async function localHolds() {
  try {
    return (await db.holds.orderBy('createdAt').reverse().toArray()).filter((h) => h.status === 'held');
  } catch {
    return [];
  }
}

/** Orders held here offline that are still waiting to sync (shown on the online list too). */
export async function unsyncedHolds() {
  try {
    return (await db.holds.toArray()).filter((h) => h.local);
  } catch {
    return [];
  }
}

export async function holdRow(clientUuid) {
  try {
    return (await db.holds.get(clientUuid)) ?? null;
  } catch {
    return null;
  }
}

/** The order was completed (offline sale) or replaced: drop it from the open list. */
export async function forgetHold(clientUuid) {
  try {
    await db.holds.delete(clientUuid);
  } catch {
    /* ignore */
  }
}

/** After POST /sync/holds: the local row becomes the server's copy (or its second copy on a conflict). */
export async function markHoldSynced(clientUuid, result) {
  try {
    await db.transaction('rw', db.holds, async () => {
      if (result.conflict) await db.holds.delete(clientUuid);
      await db.holds.put(serverRow(result.bill));
    });
  } catch {
    /* refreshed on the next refreshHolds */
  }
}

/** Does this order still have an offline save waiting to sync? */
export async function holdWaiting(clientUuid) {
  try {
    const o = await db.outbox.get(clientUuid);
    return o?.kind === 'hold' ? o : null;
  } catch {
    return null;
  }
}

/**
 * Hold the cart, or send it to the kitchen ('kot'), without internet. Returns { bill } (the
 * local row, shaped like a server bill) or throws an Error with a user-facing message. The
 * caller prints / shows the kitchen tickets.
 */
export async function holdOffline({ cart, priced, catalog, action }) {
  const me = useSession.getState().me;
  const settings = catalog.settings;
  if (!settings.offline?.allowHolds)
    throw new Error(
      i18n.t(
        'offline.holdsOff',
        'Holding orders needs an internet connection (Settings → Offline & displays). Complete the sale.',
      ),
    );
  const check = await offlineCheck();
  if (!check.ok) throw Object.assign(new Error(check.reason), { offlineReason: check.reason });
  if (cart.couponCode)
    throw new Error(
      i18n.t('offline.couponHold', 'Coupons need an internet connection. Remove the coupon to save offline.'),
    );
  if (cart.customer?.offlineNew && !settings.offline.allowNewCustomers)
    throw new Error(i18n.t('offline.newCustomer', 'Adding customers needs an internet connection.'));
  const sent = cart.sentQty ?? {};
  const reduced = Object.entries(sent).some(
    ([lineId, qty]) => qty > (cart.lines.find((l) => l.lineId === lineId)?.qty ?? 0),
  );
  if (reduced && !can(me, 'pos.void'))
    throw new Error(
      i18n.t(
        'offline.removeSent',
        'Removing items already sent to the kitchen needs a manager. Connect to the internet first.',
      ),
    );

  const at = new Date(check.serverNow ?? Date.now());
  const tokenNo = cart.tokenNo ?? (action === 'kot' ? await nextOfflineToken(check.permit, catalog, at) : null);
  const kotQty = (l) => (action === 'kot' ? l.qty : Math.min(l.qty, sent[l.lineId] ?? 0));
  const lines = cart.lines.map((l) => ({ l, p: priced.byId.get(l.lineId) })).filter((x) => x.p);
  const payload = {
    clientUuid: cart.clientUuid,
    syncId: uuid(),
    capturedAt: at.toISOString(),
    capturedBy: me.user.id,
    baseUpdatedAt: cart.baseUpdatedAt ?? null,
    tokenNo,
    orderType: cart.orderType,
    source: cart.source,
    tableNo: cart.tableNo || null,
    guestCount: cart.guestCount || null,
    customerId: cart.customer?.offlineNew ? null : (cart.customer?.id ?? null),
    newCustomer: cart.customer?.offlineNew ? { phone: cart.customer.phone, name: cart.customer.name || null } : null,
    lines: lines.map(({ l, p }) => ({
      lineId: l.lineId,
      itemId: l.itemId,
      variantId: l.variantId ?? null,
      modifierOptionIds: l.modifierOptionIds ?? [],
      qty: l.qty,
      note: l.note || null,
      discount: discountOf(l.discount),
      unitPricePaise: p.line.unitPricePaise,
      priceMode: p.line.priceMode,
      gstRateBp: p.line.gstRateBp,
      cessRateBp: p.line.cessRateBp ?? 0,
      name: p.meta.name,
      variantName: p.meta.variantName ?? null,
      kotQty: kotQty(l),
    })),
    billDiscount: cart.billDiscount
      ? { ...discountOf(cart.billDiscount), reason: cart.billDiscount.reason || null }
      : null,
    notes: cart.notes || null,
    charges: priced.chargeDefs ?? [],
    rounding: settings.rounding,
  };
  const previous = await holdRow(cart.clientUuid);
  const row = {
    id: previous?.serverId ?? `local:${cart.clientUuid}`,
    serverId: previous?.serverId ?? (cart.billId && !cart.billId.startsWith('local:') ? cart.billId : null),
    clientUuid: cart.clientUuid,
    local: true,
    status: 'held',
    baseUpdatedAt: payload.baseUpdatedAt,
    createdAt: previous?.createdAt ?? at.toISOString(),
    updatedAt: at.toISOString(),
    tokenNo,
    tableNo: payload.tableNo,
    guestCount: payload.guestCount,
    orderType: cart.orderType,
    source: cart.source,
    customer: cart.customer ?? null,
    notes: payload.notes,
    billDiscount: cart.billDiscount ?? null,
    coupon: null,
    selfOrder: previous?.selfOrder ?? null,
    lines: lines.map(({ l, p }) => ({
      lineId: l.lineId,
      itemId: l.itemId,
      variantId: l.variantId ?? null,
      name: p.meta.name,
      variantName: p.meta.variantName ?? null,
      modifiers: (p.meta.modifiers ?? []).map((m) => ({ optionId: m.optionId, name: m.name })),
      qty: l.qty,
      note: l.note || null,
      discount: l.discount ?? null,
      foodType: p.meta.foodType,
      unitPricePaise: p.line.unitPricePaise,
      kotQty: kotQty(l),
    })),
    itemCount: cart.lines.reduce((n, l) => n + l.qty, 0),
    grandTotalPaise: priced.totals.grandTotalPaise,
    totals: { grandTotalPaise: priced.totals.grandTotalPaise },
    createdBy: previous?.createdBy ?? { id: me.user.id, name: me.user.name },
  };
  await db.transaction('rw', db.outbox, db.holds, async () => {
    await db.holds.put(row);
    await db.outbox.put({
      clientUuid: cart.clientUuid,
      kind: 'hold',
      status: 'pending',
      createdAt: Date.now(),
      attempts: 0,
      payload,
      label: tokenNo ? `Open order · Token ${tokenNo}` : 'Open order',
      totalPaise: priced.totals.grandTotalPaise,
    });
  });
  return { bill: row };
}
